import {
  createContext,
  Fragment,
  useContext,
  useState,
  type ReactNode,
} from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import Canvas from '@/designs-dev/handover/Canvas';
import { Segment } from '@/designs-dev/inspect/Pill';
import { ConceptTag } from '@/designs-dev/loft/conceptTag';
import Panel, { SectionTitle } from '@/designs-dev/handover/Panel';
import MiniScreen, { miniSize } from '@/designs-dev/handover/MiniScreen';
import LoopFrame from '@/designs-dev/handover/LoopFrame';
import ScrollRow from '@/designs-dev/handover/ScrollRow';
import {
  type Flow,
  type FlowAlt,
  type HandoverSpec,
  type Interaction,
  type Pattern,
  type PatternGroup,
  type SpecTable,
  type StateCase,
  type StateKind,
} from '@/designs-dev/handover/types';

const PATTERN_GROUP_LABEL: Record<PatternGroup, string> = {
  ask: 'How we ask',
  choose: 'Choosing and replying',
  answer: 'How they answer',
  read: 'How we show what we read',
  keep: 'Where it lands',
  layout: 'How a screen sits',
};

const KIND_LABEL: Record<StateKind, string> = {
  view: 'Views',
  happy: 'Happy path',
  empty: 'Empty states',
  edge: 'Edge cases',
  error: 'Error states',
};

// Fills the width it is given without asking for any. On a canvas the page is as
// wide as its widest flow, and anything that wraps (a paragraph, a grid of
// states, a table) would otherwise ask for its whole length on one line and
// make the page that wide instead. Changes nothing on a page that scrolls,
// where the width comes from the window.
const FILL = { contain: 'inline-size' } as const;

// A small icon hint per link: handovers get a doc, external links the
// open-in-new arrow, internal prototype routes a play triangle.
const linkIcon = (url: string) =>
  url.startsWith('/handover')
    ? 'file-lines'
    : url.startsWith('http')
      ? 'arrow-up-right-from-square'
      : 'play';

// A section that folds. What a long reference table is made of: one per part of
// it, so the parts are the page and the rows are behind them.
//
// Stock Accordion, nothing overridden. The theme already makes one a bordered
// neutral.50 card at radius.md, with a 56px summary and the inset MUI 9 dropped,
// so there is nothing here to say. The version of this that set
// `disableGutters`, a transparent background and `px: 0` was undoing all of
// that, which is what made the labels sit hard against the panel gutter with
// nothing saying they were controls.
const Fold = ({
  label,
  defaultExpanded = false,
  children,
}: {
  label: string;
  defaultExpanded?: boolean;
  children: ReactNode;
}) => (
  <Accordion defaultExpanded={defaultExpanded}>
    <AccordionSummary
      expandIcon={<Icon icon={'chevron-down' as never} fontSize={14} />}
    >
      <SectionTitle label={label} />
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

// The rows of one table. Its own component so a sectioned table can render one
// per section without the header being written out twice.
const TableRows = ({
  columns,
  rows,
}: {
  columns: string[];
  rows: string[][];
}) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Table size='small'>
      <TableHead>
        <TableRow>
          {columns.map((c) => (
            <TableCell key={c}>{c}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => (
              <TableCell key={j} sx={{ verticalAlign: 'top' }}>
                <Typography
                  variant='body2'
                  color={j === 0 ? 'text.primary' : 'text.secondary'}
                >
                  {cell}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

/**
 * A reference table, whole or by section.
 *
 * Sectioned, it is a fold per section: the labels stack as a list you read
 * down, and the rows of one are behind it. A 227-row table is a page you scroll
 * past rather than read, and the section you want is behind every section you
 * do not.
 *
 * It was a pick-one above the table first. Seventeen sections is too many for
 * that: the row of buttons wrapped into a wall three deep, which is a thing to
 * get past before the table rather than a way into it, and picking one hid the
 * sixteen you had just read. Folds keep every label in one column and let two
 * be open at once.
 */
const SpecTablePanel = ({ table }: { table: SpecTable }) => {
  const groups = table.groups ?? [];

  return (
    <Panel label={table.title}>
      {table.caption && (
        <Typography variant='caption' color='text.secondary'>
          {table.caption}
        </Typography>
      )}

      {groups.length > 0 ? (
        // The first one open, so the panel shows what a section holds rather
        // than only what the sections are called.
        // No spacing on the column: the theme already puts 8px under every
        // fold but the last, and a Stack gap on top of it double-spaced them.
        <Stack>
          {groups.map((group, i) => (
            <Fold
              key={group.label}
              label={group.label}
              defaultExpanded={i === 0}
            >
              <TableRows columns={table.columns} rows={group.rows} />
            </Fold>
          ))}
        </Stack>
      ) : (
        <TableRows columns={table.columns} rows={table.rows ?? []} />
      )}
    </Panel>
  );
};

// A part of the page under a rule and a heading, shut or open from the
// heading. The chevron leads the title, the way a layer group's does.
const HeadedRow = ({
  heading,
  collapsible = true,
  first = false,
  children,
}: {
  heading: string;
  // Whether the heading opens and shuts the row under it. Off for a page that
  // asks for fixed headings.
  collapsible?: boolean;
  // The first thing on the page already sits under the intro's rule, so a
  // second rule here would draw two lines one above the other.
  first?: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(true);
  return (
    <Stack spacing={3}>
      {!first && <Divider />}
      {!collapsible ? (
        <Typography variant='h4'>{heading}</Typography>
      ) : (
        <Stack
          component='button'
          type='button'
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          direction='row'
          spacing={1.5}
          sx={{
            alignItems: 'center',
            alignSelf: 'flex-start',
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            cursor: 'pointer',
            color: 'text.primary',
            textAlign: 'left',
          }}
        >
          <Icon
            icon={(open ? 'chevron-down' : 'chevron-right') as never}
            fontSize={16}
          />
          <Typography variant='h4'>{heading}</Typography>
        </Stack>
      )}
      {(open || !collapsible) && children}
    </Stack>
  );
};

// One flow: a titled row of minis with an arrow and the tap between them.
// A step's frame stepped through its variants, a chevron either side at the
// frame's middle. Wraps round, so the last leads back to the first.
const Variants = ({
  items,
  top,
  children,
}: {
  items: FlowAlt[];
  /** How far down the chevrons sit: the frame's middle. */
  top: number;
  children: (item: FlowAlt, n: number, of: number) => ReactNode;
}) => {
  const [n, setN] = useState(0);
  const go = (by: number) => setN((n + by + items.length) % items.length);
  const chevron = (by: number, icon: string, label: string) => (
    <IconButton
      aria-label={label}
      onClick={() => go(by)}
      size='small'
      sx={{ alignSelf: 'flex-start', mt: `${top - 16}px` }}
    >
      <Icon icon={icon as never} fontSize={16} />
    </IconButton>
  );
  return (
    <Stack direction='row' spacing={0.5}>
      {chevron(-1, 'chevron-left', 'Previous variant')}
      {children(items[n], n, items.length)}
      {chevron(1, 'chevron-right', 'Next variant')}
    </Stack>
  );
};

// Whether a flow's notes show: the readmes beside a flow and the notes under
// its steps. Off, the page is just the screens, for looking at the work
// without the reasoning in the way. Set from the bar.
const NotesShown = createContext(true);
const NOTES_KEY = 'loft:flow-notes';
// Remembered per browser, so a page opens the way it was left. Storage can be
// missing or blocked (a private window); then notes just start on.
const readNotes = () => {
  try {
    return localStorage.getItem(NOTES_KEY) !== 'off';
  } catch {
    return true;
  }
};

const FlowStrip = ({
  flow,
  screen,
  showTitle = true,
}: {
  flow: Flow;
  screen: 'phone' | 'desktop';
  /** Off when the flow's card is already labelled with its title. */
  showTitle?: boolean;
}) => {
  const variant = flow.screen ?? screen;
  const notes = useContext(NotesShown);

  // A grid rather than a row of columns, so the strip has actual rows: every
  // step's mini is in the first, every alt in the second, every note in the
  // third. Stacking each step in its own column looked the same until a step
  // title wrapped to two lines, which pushed that column's alt down and left
  // the b row stepped. Grid rows are as tall as their tallest cell, so the
  // alts line up whatever the labels above them do.
  //
  // Two rows, not three. A note used to have a row of its own, which put it
  // below the tallest alt in the whole strip: a note on a step with no alt
  // floated a mini's height away from the step it belonged to, under somebody
  // else's column. It rides with the alt instead, so whatever a step has to say
  // stays under the step.
  //
  // Auto-flow column fills top to bottom then moves right, so every column has
  // to contribute both cells in order. A step with nothing underneath
  // contributes an empty one.
  const cells: ReactNode[] = [];
  const column = (top: ReactNode, under?: ReactNode) => {
    cells.push(top, under ?? null);
  };

  if (flow.note && notes) {
    // A screen and a half: wide enough to read a paragraph in, narrow enough
    // that it stays part of the row rather than heading it.
    column(
      <Box
        sx={{ width: `${Math.round(miniSize(variant).width * 1.5)}px`, mr: 2 }}
      >
        {flow.note}
      </Box>,
    );
  }

  flow.steps.forEach((step, i) => {
    const alts = step.alt
      ? Array.isArray(step.alt)
        ? step.alt
        : [step.alt]
      : [];

    if (i > 0 && flow.arrows === false && !step.tap) {
      // No arrow and nothing to say, so just a gap between the minis.
      column(<Box sx={{ width: 24 }} />);
    } else if (i > 0) {
      column(
        <Stack
          sx={{
            alignItems: 'center',
            // Just above the middle of the frame beside it, worked out from the
            // mini's own height rather than pinned to a number that only suited
            // one scale.
            pt: `${Math.round(miniSize(variant).height * 0.4)}px`,
            width: 88,
          }}
          spacing={0.5}
        >
          {flow.arrows !== false && (
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>
              <Icon icon={'long-arrow-right' as never} fontSize={20} />
            </Box>
          )}
          {step.tap && (
            <Typography
              sx={{ textAlign: 'center' }}
              variant='caption'
              color='text.secondary'
            >
              {step.tap}
            </Typography>
          )}
        </Stack>,
      );
    }

    const stepMini = (title: string, body: ReactNode, caption?: string) => (
      <MiniScreen
        label={flow.numbered === false ? title : `${i + 1} · ${title}`}
        isNew={step.isNew}
        tint={step.tint}
        caption={caption ?? (i === 0 ? step.tap : undefined)}
        variant={variant}
        pointer={{ kind: 'Step', id: step.id, group: flow.title }}
      >
        {body}
      </MiniScreen>
    );
    const mini = step.variants ? (
      <Variants
        items={[{ title: step.title, render: step.render }, ...step.variants]}
        top={Math.round(miniSize(variant).height / 2)}
      >
        {(item, n, of) =>
          stepMini(
            item.title,
            // Keyed, so a screen that reads its props once starts afresh.
            <Fragment key={n}>{item.render()}</Fragment>,
            `${n + 1} of ${of}`,
          )
        }
      </Variants>
    ) : (
      stepMini(step.title, step.render())
    );
    column(
      step.highlight ? (
        // Drawn out into the gap rather than inside it, so a highlighted step
        // sits on the same line as the rest of the row.
        <Box
          sx={{
            m: -1.5,
            p: 1.5,
            bgcolor: step.highlight,
            borderRadius: `${radius.md}px`,
          }}
        >
          {mini}
        </Box>
      ) : (
        mini
      ),
      (step.alt || (step.note && notes)) && (
        <Stack
          sx={{
            alignItems: 'flex-start',
            // Back up to the old gap when there is an alt: two readings of one
            // step have to be told apart from two consecutive steps. A note on
            // its own wants the opposite, which is why the row gap is set for
            // the note and the alt pays the difference here.
            pt: alts.length ? 3 : 0,
          }}
          spacing={2}
        >
          {alts.map((alt, j) => (
            <MiniScreen
              key={alt.title}
              // b, c, d in the order the spec gives them.
              label={`${i + 1}${String.fromCharCode(98 + j)} · ${alt.title}`}
              isNew={alt.isNew}
              variant={variant}
              // An alt has no id of its own in the spec; it is reached through
              // the step's `alt`, so that is what it points at. A lone alt keeps
              // the unsuffixed id it has always had.
              pointer={{
                kind: 'Step',
                id:
                  alts.length > 1
                    ? `${step.id} → alt ${String.fromCharCode(98 + j)}`
                    : `${step.id} → alt`,
                group: flow.title,
              }}
            >
              {alt.render()}
            </MiniScreen>
          ))}
          {step.note && notes && (
            <Box sx={{ width: `${miniSize(variant).width}px` }}>
              {step.note}
            </Box>
          )}
        </Stack>
      ),
    );
  });

  if (flow.noteAfter && notes) {
    // Same width as a readme on the left, in the column after the last step:
    // beside the screen it is about rather than under whatever that screen has
    // stacked below it.
    column(
      <Box
        sx={{ width: `${Math.round(miniSize(variant).width * 1.5)}px`, ml: 2 }}
      >
        {flow.noteAfter}
      </Box>,
    );
  }

  return (
    <Stack spacing={1.5}>
      <ScrollRow title={showTitle ? flow.title : undefined}>
        <Box
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'max-content',
            gridTemplateRows: 'auto auto',
            alignItems: 'start',
            columnGap: 2,
            // Set for the closest of the two things that sit under a step: its
            // note, which belongs to it and reads as part of it. An alt needs
            // more air than this, because the b row would otherwise read as the
            // next thing that happens, so it adds its own below.
            rowGap: 3,
          }}
        >
          {/* A real element per cell, empty ones included: a slot that renders
              nothing is a slot the auto-flow skips, which would pull the next
              step's mini up into the b row. */}
          {cells.map((cell, i) => (
            <Box key={i}>{cell}</Box>
          ))}
        </Box>
      </ScrollRow>
    </Stack>
  );
};

// The states of one section, grouped by kind. Generous gaps between the groups
// so they read as sub-sections rather than one wall of minis.
const StateGrid = ({
  cases,
  screen,
}: {
  cases: StateCase[];
  screen: 'phone' | 'desktop';
}) => (
  // No FILL: the panel hugs, so the grid asks for its own width and wraps only
  // at the page's.
  <Stack spacing={6}>
    {(['view', 'happy', 'empty', 'edge', 'error'] as StateKind[]).map(
      (kind) => {
        const group = cases.filter((c) => c.kind === kind);
        if (group.length === 0) return null;
        // The rows this kind asks for, in the order the spec lists them, with
        // anything untagged leading. A kind nobody split still renders as one
        // row, exactly as it did.
        const rows: (string | undefined)[] = [];
        for (const c of group) {
          if (!rows.includes(c.row)) rows.push(c.row);
        }
        rows.sort((a, b) => (a === undefined ? -1 : b === undefined ? 1 : 0));
        // "Happy path" is the absence of a qualifier: everything here is the
        // happy path unless it says otherwise, so once the rows are named the
        // heading is a word above a word. Error states keeps its heading,
        // because that one is telling you something.
        const named = rows.every(Boolean);
        const heading = kind === 'happy' && named ? null : KIND_LABEL[kind];
        return (
          <Stack key={kind} spacing={1.5}>
            {/* h4 so the group heading reads clearly above the mini labels,
              which are subtitle2 */}
            {heading && <Typography variant='h4'>{heading}</Typography>}
            {rows.map((row) => (
              <Stack key={row ?? '_'} spacing={1.5}>
                {/* subtitle1 sits between the kind's h4 and the minis'
                    subtitle2, so a row reads as part of the kind rather than as
                    a section of its own. */}
                {row && (
                  <Typography
                    sx={{ pt: heading ? 2 : 0 }}
                    variant={heading ? 'subtitle1' : 'h4'}
                  >
                    {row}
                  </Typography>
                )}
                <Stack
                  sx={{ columnGap: 2, rowGap: 5, flexWrap: 'wrap' }}
                  direction='row'
                  useFlexGap
                >
                  {group
                    .filter((c) => c.row === row)
                    .map((c) => (
                      <MiniScreen
                        key={c.id}
                        label={c.title}
                        isNew={c.isNew}
                        caption={c.repro}
                        variant={c.screen ?? screen}
                        pointer={{
                          kind: 'State',
                          id: c.id,
                          group: row
                            ? `${KIND_LABEL[kind]} / ${row}`
                            : KIND_LABEL[kind],
                        }}
                      >
                        {c.render()}
                      </MiniScreen>
                    ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        );
      },
    )}
  </Stack>
);

// The patterns a feature establishes, in the order an intake goes through them.
// A wrapping grid rather than a row: patterns have no sequence. Name and screen
// only, so the row reads as a vocabulary rather than a document.
const PatternGrid = ({
  patterns,
  screen,
  readme,
}: {
  patterns: Pattern[];
  screen: 'phone' | 'desktop';
  /** Beside the first row, at the width a flow's readme gets. */
  readme?: React.ReactNode;
}) => (
  // No FILL, so the panel can hug it. See StateGrid.
  <Stack spacing={6}>
    {(
      ['ask', 'choose', 'answer', 'read', 'keep', 'layout'] as PatternGroup[]
    ).map((group, groupIndex) => {
      const inGroup = patterns.filter((p) => p.group === group);
      if (inGroup.length === 0) return null;
      return (
        <Stack key={group} spacing={1.5}>
          <Typography variant='h4'>{PATTERN_GROUP_LABEL[group]}</Typography>
          <Stack
            sx={{ columnGap: 2, rowGap: 5, flexWrap: 'wrap' }}
            direction='row'
            useFlexGap
          >
            {inGroup.map((p, i) => (
              // A note sits to the right of the mini it belongs to, as its
              // own column. Under the mini it made that column twice as tall
              // as its neighbours; at the head of the row it stopped being
              // about the screen beside it.
              <Fragment key={p.id}>
                <MiniScreen
                  label={p.name}
                  isNew={p.isNew}
                  caption={p.when}
                  variant={p.screen ?? screen}
                  pointer={{
                    kind: 'Pattern',
                    id: p.id,
                    group: PATTERN_GROUP_LABEL[group],
                  }}
                >
                  {p.render()}
                </MiniScreen>
                {p.note && (
                  <Box
                    sx={{
                      width: `${Math.round(miniSize(screen).width * 1.5)}px`,
                    }}
                  >
                    {p.note}
                  </Box>
                )}
                {/* To the right of the first screen rather than across the
                    page or ahead of it: the screen comes first, and the
                    principles read as notes on what is being looked at. */}
                {readme && groupIndex === 0 && i === 0 && (
                  <Box
                    sx={{
                      width: `${Math.round(miniSize(screen).width * 1.5)}px`,
                      mx: 2,
                    }}
                  >
                    {readme}
                  </Box>
                )}
              </Fragment>
            ))}
          </Stack>
        </Stack>
      );
    })}
  </Stack>
);

// The movements, each playing itself. One row, wrapping: there is no spine to
// group these by, and there will never be many — a flow with a dozen animations
// in it has a different problem.
//
// The note sits beside the mini rather than under it, the way a pattern's does,
// because what it holds is timings and those are read against the thing moving.
const InteractionGrid = ({
  interactions,
  screen,
}: {
  interactions: Interaction[];
  screen: 'phone' | 'desktop';
}) => (
  // Wider apart than patterns: each carries a sentence under it, and close
  // together the sentences ran into one another.
  <Stack
    sx={{ columnGap: 5, rowGap: 5, flexWrap: 'wrap' }}
    direction='row'
    useFlexGap
  >
    {interactions.map((i) => (
      <Fragment key={i.id}>
        {/* With a bar under it showing where in the loop it is, and play,
            pause and loop, so a jumpy start can be seen to be the start. */}
        <LoopFrame width={miniSize(i.screen ?? screen).width}>
          <MiniScreen
            label={i.name}
            caption={i.what}
            variant={i.screen ?? screen}
            pointer={{ kind: 'Interaction', id: i.id }}
          >
            {i.render()}
          </MiniScreen>
        </LoopFrame>
        {i.note && (
          <Box sx={{ width: `${Math.round(miniSize(screen).width * 1.5)}px` }}>
            {i.note}
          </Box>
        )}
      </Fragment>
    ))}
  </Stack>
);

// All flows: the flow and every state on one
// page, rendered live from the prototype components so it can't go stale. Fed
// entirely by a HandoverSpec; writing the next feature's handover means writing a
// spec file, not a page.
//
// Designs and flows only. The why, the user stories and the success metrics were
// all here and all came off: they live in the mini PRD and the feature spec, and a
// second copy in the prototype is a second thing to keep true. The links panel is
// how you get to them.
const HandoverPage = ({
  spec,
  version,
  tools,
}: {
  spec: HandoverSpec;
  /** The breadcrumb's last step: which version this is, for a versioned page. */
  version?: ReactNode;
  /** The page's own controls for the canvas's floating bar. */
  tools?: ReactNode;
}) => {
  const concept = useContext(ConceptTag);
  const [notes, setNotes] = useState(readNotes);
  const toggleNotes = () => {
    const next = !notes;
    setNotes(next);
    try {
      localStorage.setItem(NOTES_KEY, next ? 'on' : 'off');
    } catch {
      // Best effort: without storage it still toggles, it just isn't
      // remembered next time. Nothing to report.
    }
  };
  const screen = spec.screen ?? 'phone';
  // The prototype in place of the page. State, not a route: the trial is that
  // looking at the prototype does not take you anywhere.
  const [viewing, setViewing] = useState(false);

  // Journeys in the order the spec introduces them, flows first. Derived rather
  // than declared: the order a spec lists its flows in is already the order it
  // wants them read.
  const journeys: string[] = [];
  for (const item of [...spec.flows, ...spec.states]) {
    if (item.journey && !journeys.includes(item.journey)) {
      journeys.push(item.journey);
    }
  }
  // Anything parked reads last, whatever order the spec lists it in. A journey
  // that is not being built should not sit between two that are, and moving it
  // in the spec would move it away from the flow it was lifted out of.
  const parked = (journey: string) => /^parked\b/i.test(journey);
  const live = journeys.filter((j) => !parked(j));
  const held = journeys.filter(parked);
  const looseFlows = spec.flows.filter((f) => !f.journey);
  const looseStates = spec.states.filter((s) => !s.journey);

  const journeyPanel = (journey: string) => {
    const flows = spec.flows.filter((f) => f.journey === journey);
    const states = spec.states.filter((s) => s.journey === journey);
    return (
      <Panel
        key={journey}
        label={`${journey.toUpperCase()} JOURNEY`}
        quiet={parked(journey)}
        collapsible={parked(journey)}
      >
        <Stack spacing={6}>
          {flows.map((flow) => (
            <FlowStrip key={flow.title} flow={flow} screen={screen} />
          ))}
          {states.length > 0 && (
            <>
              <Divider />
              <StateGrid cases={states} screen={screen} />
            </>
          )}
        </Stack>
      </Panel>
    );
  };

  const page = (
    <Box sx={{ px: 4, py: 5 }}>
      {/* breadcrumb header. */}
      <Stack sx={{ alignItems: 'center', mb: 4 }} direction='row' spacing={1}>
        <Typography variant='overline' color='text.secondary'>
          All flows
        </Typography>
        <Typography variant='overline' color='text.secondary'>
          /
        </Typography>
        <Typography variant='overline'>{spec.title}</Typography>
        {/* Which concept, as the last step of where you are, the way a
            versioned page shows its version. */}
        {concept && (
          <>
            <Typography variant='overline' color='text.secondary'>
              /
            </Typography>
            <Typography variant='overline'>{concept}</Typography>
          </>
        )}
        {/* Which version, as the last step of where you are. */}
        {version && (
          <>
            <Typography variant='overline' color='text.secondary'>
              /
            </Typography>
            {version}
          </>
        )}
      </Stack>

      {/* intro: title and description, with the links panel alongside. */}
      <Stack
        sx={{ ...FILL, alignItems: 'flex-start' }}
        direction='row'
        spacing={6}
        divider={<Divider orientation='vertical' flexItem />}
      >
        {/* Title and description only. The rule that used to sit under them
            separated the description from the why, and with the why gone it was a
            line with nothing on the other side of it. */}
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1.5}>
          <Typography variant='h1'>{spec.title}</Typography>
          {spec.prototype && (
            <Box>
              <Button
                variant='secondary'
                size='sm'
                ariaLabel='View prototype'
                iconLeft={'play' as never}
                onPress={() => setViewing(true)}
              >
                View prototype
              </Button>
            </Box>
          )}
          <Typography variant='body1' color='text.secondary'>
            {spec.description}
          </Typography>
        </Stack>

        {/* sidebar */}
        <Stack
          sx={{ width: 360, flexShrink: 0 }}
          spacing={4}
          divider={<Divider />}
        >
          {spec.links.length > 0 && (
            <Stack spacing={1.5}>
              <SectionTitle label='LINKS' />
              <Stack spacing={1}>
                {spec.links.map((l) => (
                  <Stack
                    sx={{ alignItems: 'center', color: 'text.secondary' }}
                    key={l.url}
                    direction='row'
                    spacing={1}
                  >
                    <Icon icon={linkIcon(l.url) as never} fontSize={14} />
                    <Link
                      href={l.url}
                      target='_blank'
                      rel='noopener'
                      variant='body2'
                      underline='hover'
                    >
                      {l.label}
                    </Link>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ my: 5 }} />

      <Stack spacing={5}>
        {/* One section per journey when the spec tags them, each carrying its
            own flows and then its own states. A multi-sided feature used to put
            every flow in one panel and every state in another, which left an
            engineer building the provider's page reading past the CAM's table and
            the family's phone to find the four frames they needed.
            Untagged flows and states fall through to the panels below, so a
            single-audience handover renders exactly as it always did. */}
        {live.map(journeyPanel)}

        {/* Rows of columns of lines of sections. A flow marked `beside` starts
            a new column in the row before it; one marked `under` starts a new
            line in the column before it; one marked `alongside` joins the line
            before it. Anything else starts a row of its own. */}
        {looseFlows
          .reduce<Flow[][][][]>((rows, flow) => {
            const row = rows[rows.length - 1];
            const column = row?.[row.length - 1];
            if (flow.alongside && column) column[column.length - 1].push(flow);
            else if (flow.under && column) column.push([flow]);
            else if (flow.beside && row) row.push([[flow]]);
            else rows.push([[[flow]]]);
            return rows;
          }, [])
          .map((row, i) => {
            const sections = (
              <Stack
                sx={{ alignItems: 'flex-start' }}
                direction='row'
                spacing={5}
              >
                {row.map((column) => (
                  <Stack key={column[0][0].title} spacing={5}>
                    {column.map((line) => (
                      <Stack
                        key={line[0].title}
                        sx={{ alignItems: 'flex-start' }}
                        direction='row'
                        spacing={5}
                      >
                        {line.map((flow) => (
                          <Panel
                            key={flow.title}
                            label={flow.title.toUpperCase()}
                            collapsible={flow.collapsed}
                            isNew={flow.isNew}
                            background={flow.background}
                            hug
                            action={
                              flow.present && (
                                // Opens the flow presented. Its own click, so it
                                // never toggles a collapsible panel.
                                <IconButton
                                  component={RouterLink}
                                  to={flow.present}
                                  aria-label={`Present ${flow.title}`}
                                  size='small'
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ color: 'text.secondary' }}
                                >
                                  <Icon icon={'play' as never} fontSize={11} />
                                </IconButton>
                              )
                            }
                          >
                            <FlowStrip
                              flow={flow}
                              screen={screen}
                              showTitle={false}
                            />
                          </Panel>
                        ))}
                      </Stack>
                    ))}
                  </Stack>
                ))}
              </Stack>
            );
            // A row can start a new part of the page: a rule, then its
            // heading, which opens and shuts the row under it.
            const heading = row[0][0][0].heading;
            return heading ? (
              <HeadedRow
                key={row[0][0][0].title}
                heading={heading}
                collapsible={!spec.fixedHeadings}
                first={i === 0 && live.length === 0}
              >
                {sections}
              </HeadedRow>
            ) : (
              <Fragment key={row[0][0][0].title}>
                {row[0][0][0].divider && <Divider />}
                {sections}
              </Fragment>
            );
          })}

        {/* Patterns after the flows: you read what happens, then what the
            pieces are and when to use them. */}
        {spec.patterns?.length ? (
          <Panel label='PATTERNS' hug>
            <PatternGrid
              patterns={spec.patterns}
              screen={screen}
              readme={spec.readme}
            />
          </Panel>
        ) : null}

        {/* After the patterns, because a movement is between two of them: you
            have to know the bars before the change from one to the other is
            about anything. These play on a loop while they are on screen. */}
        {spec.interactions?.length ? (
          <Panel label='INTERACTION DESIGN' hug>
            <InteractionGrid interactions={spec.interactions} screen={screen} />
          </Panel>
        ) : null}

        {looseStates.length > 0 && (
          <Panel label='STATES & EDGE CASES' hug>
            <StateGrid cases={looseStates} screen={screen} />
          </Panel>
        )}

        {/* Below the states: it is not part of what is being built, so it
            should not be read on the way to something that is. */}
        {held.map(journeyPanel)}

        {/* Reference tables last, under every screen on the page. They used to
            head it, on the argument that the scope should read first. They are
            not scope: the AA table is 227 rows of the DWP's own wording, and
            putting it above the flows meant scrolling past the whole form to
            reach the design. It is a thing you come back to look something up
            in, so it sits where a reference does. */}
        {spec.tables?.map((t) => (
          <Box key={t.title} sx={FILL}>
            <SpecTablePanel table={t} />
          </Box>
        ))}
      </Stack>
    </Box>
  );

  if (spec.prototype && viewing) {
    return (
      <Box sx={{ position: 'relative' }}>
        {spec.prototype()}
        <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 3 }}>
          <Button
            variant='secondary'
            size='sm'
            ariaLabel='Back to flows'
            iconLeft={'chevron-left' as never}
            onPress={() => setViewing(false)}
          >
            Back to flows
          </Button>
        </Box>
      </Box>
    );
  }

  // Every flows page pans and zooms unless it says otherwise.
  // The bar says what the page is: a versioned page brings its version, and a
  // concept says which concept, so exploring is never mistaken for building.
  // Then the notes switch, on every flows page.
  const pageTools =
    tools ??
    (concept ? <Segment label='Concept'>{concept}</Segment> : undefined);
  const barTools = (
    <>
      {pageTools}
      {pageTools && <Divider orientation='vertical' flexItem />}
      <Segment
        icon={notes ? 'eye' : 'eye-slash'}
        label={notes ? 'Hide notes' : 'Show notes'}
        selected={notes}
        onClick={toggleNotes}
      >
        Notes
      </Segment>
    </>
  );
  return (
    <NotesShown.Provider value={notes}>
      {spec.canvas === false ? page : <Canvas tools={barTools}>{page}</Canvas>}
    </NotesShown.Provider>
  );
};

export default HandoverPage;
